import React from "react";
import { GameMessageEvent, GameState } from "../../../core/protocol/types";
import MessageAnimation from "./animations/MessageAnimation";
import { MESSAGE_VARIANT_COLORS } from "../../../core/constants";
import { AnnouncerPackRegistry } from "../../../core/engine/packs/announcers/AnnouncerPackRegistry";
import { AnnouncerPackFactory, AnnouncerPackType } from "../../../core/engine/packs/announcers/AnnouncerPackFactory";
import DefaultAnnouncerPack from "../../../core/engine/packs/announcers/DefaultAnnouncerPack";
import { AudioPlaybackHandle } from "../../../core/engine/types/audio-manager";
import { IAudioManager } from "../../../core/engine/types/audio-manager";

// Create singleton instances
const factory = new AnnouncerPackFactory();
const registry = new AnnouncerPackRegistry(factory);
let initializedAudioManager: IAudioManager | null = null;

const getSavedFragPack = (): AnnouncerPackType => {
  try {
    const stored = localStorage.getItem("fragAnnouncerPack");
    return stored ? (JSON.parse(stored) as AnnouncerPackType) : "default";
  } catch {
    return "default";
  }
};

export const initializeAnnouncerAudio = (audioManager: IAudioManager): void => {
  if (initializedAudioManager === audioManager) {
    return;
  }
  initializedAudioManager = audioManager;
  factory.initialize(audioManager);
  factory.preloadPack("default");
  const savedPack = getSavedFragPack();
  if (savedPack !== "default") {
    factory.preloadPack(savedPack);
  }
};

/**
 * Context passed to message animation handlers
 */
export interface MessageAnimationContext {
  message: GameMessageEvent;
  localConnectionId: string;
  localTeam: number;
  gameState: GameState;
  onComplete: () => void;
  /**
   * Optional callback for animations to trigger audio playback.
   * Returns a playback handle so animations can stop or analyse the audio.
   */
  playAudio?: () => AudioPlaybackHandle | null;
  /**
   * Optional audio name for direct audio manager integration / debugging.
   */
  audioName?: string;
}

/**
 * Message animation handler type
 * Takes context and returns a React element that handles its own rendering and animation
 */
export type MessageAnimationHandler = (
  context: MessageAnimationContext
) => React.ReactElement;

/**
 * Frag message handler that uses the frag announcer pack system
 * Resolves player names and fragAnnouncerPack from gameState using numPlayer
 */
const FragHandler = (context: MessageAnimationContext): React.ReactElement => {
  const { message, localConnectionId, gameState, onComplete } = context;

  // Handler called silently

  // Type guard to ensure this is a frag message
  if (message.type !== "frag") {
    console.error("[FragHandler] Invalid message type:", message.type);
    onComplete();
    return <></>;
  }

  // Resolve fragger and fragged players from gameState using numPlayer
  let fragger: { connectionId: string; name: string; fragAnnouncerPack: string } | undefined;
  let fragged: { connectionId: string; name: string } | undefined;

    for (const [, player] of gameState.players.entries()) {
      if (player.numPlayer === message.fraggerNumPlayer) {
        fragger = {
          connectionId: player.connectionId,
          name: player.name || "Unknown",
          fragAnnouncerPack: player.fragAnnouncerPack || "default",
        };
      }
      if (player.numPlayer === message.fraggedNumPlayer) {
        fragged = {
          connectionId: player.connectionId,
          name: player.name || "Unknown",
        };
      }
    }

  if (!fragger || !fragged) {
    console.warn(`[FragHandler] Could not resolve players: fraggerNumPlayer=${message.fraggerNumPlayer}, fraggedNumPlayer=${message.fraggedNumPlayer}`);
    onComplete();
    return <></>;
  }

  // Check if local player is the fragger
  const isFragger = fragger.connectionId === localConnectionId;

  // For fraggers, always use default (text) animation
  if (isFragger) {
    const defaultPack = new DefaultAnnouncerPack();
    const handler = defaultPack.getAnimationHandler("frag", context);
    if (handler) {
      return handler(context);
    }
    onComplete();
    return <></>;
  }

  // For victims, use the fragger's pack preference
  // Handle case where it might be stored as a JSON string with quotes
  let fragAnnouncerPack = fragger.fragAnnouncerPack || "default";
  if (fragAnnouncerPack.startsWith('"') && fragAnnouncerPack.endsWith('"')) {
    fragAnnouncerPack = fragAnnouncerPack.slice(1, -1);
  }
  fragAnnouncerPack = fragAnnouncerPack.trim().toLowerCase();

  // Get the pack based on the fragger's preference (not the local player's preference)
  const pack = factory.getFragAnnouncerPack(fragAnnouncerPack as AnnouncerPackType);
  const handler = pack.getAnimationHandler("frag", context);
  if (handler) {
    return handler(context);
  }

  // Fallback
  onComplete();
  return <></>;
};

/**
 * Round win message handler
 */
const RoundWinHandler = (
  context: MessageAnimationContext
): React.ReactElement => {
  const pack = registry.getPackForEventType("roundWin");
  const handler = pack.getAnimationHandler("roundWin", context);
  if (handler) {
    return handler(context);
  }
  // Fallback to default behavior if no handler
  const { message, onComplete } = context;
  if (message.type !== "roundWin") {
    onComplete();
    return <></>;
  }
  return (
    <MessageAnimation
      text={`team ${message.winningTeamName} won`}
      headerText="VICTORY"
      headerColor={MESSAGE_VARIANT_COLORS.positive}
      headerFontSize="clamp(20px, 4vw, 30px)"
      fontSize="clamp(24px, 6vw, 48px)"
      color="#FFFFFF"
      textShadow="0 2px 8px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 255, 255, 0.3)"
      onComplete={onComplete}
    />
  );
};

/**
 * Round lose message handler
 */
const RoundLoseHandler = (
  context: MessageAnimationContext
): React.ReactElement => {
  const pack = registry.getPackForEventType("roundLose");
  const handler = pack.getAnimationHandler("roundLose", context);
  if (handler) {
    return handler(context);
  }
  // Fallback to default behavior if no handler
  const { message, onComplete } = context;
  if (message.type !== "roundLose") {
    onComplete();
    return <></>;
  }
  return (
    <MessageAnimation
      text={`team ${message.winningTeamName} won`}
      headerText="DEFEATED"
      headerColor={MESSAGE_VARIANT_COLORS.negative}
      headerFontSize="clamp(20px, 4vw, 30px)"
      fontSize="clamp(24px, 6vw, 48px)"
      color="#FFFFFF"
      textShadow="0 2px 8px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 255, 255, 0.3)"
      onComplete={onComplete}
    />
  );
};

/**
 * Registry of message animation handlers
 * Maps message type to its animation handler function
 *
 * To add a new message type:
 * 1. Add the type to GameMessageEvent in types.ts
 * 2. Create a new animation component
 * 3. Register it here
 *
 * To add a new animation variant for an existing type:
 * 1. Create the animation component
 * 2. Update the handler function to select based on user preference
 */
/**
 * First blood message handler
 */
const FirstBloodHandler = (
  context: MessageAnimationContext
): React.ReactElement => {
  const pack = registry.getPackForEventType("firstBlood");
  const handler = pack.getAnimationHandler("firstBlood", context);
  if (handler) {
    return handler(context);
  }
  // Fallback
  const { message, onComplete } = context;
  if (message.type !== "firstBlood") {
    onComplete();
    return <></>;
  }
  // Use default pack's firstBlood handler
  const defaultPack = new DefaultAnnouncerPack();
  const defaultHandler = defaultPack.getAnimationHandler("firstBlood", context);
  if (defaultHandler) {
    return defaultHandler(context);
  }
  onComplete();
  return <></>;
};

/**
 * Multi-kill message handler
 */
const MultiKillHandler = (
  context: MessageAnimationContext
): React.ReactElement => {
  const pack = registry.getPackForEventType("multiKill");
  const handler = pack.getAnimationHandler("multiKill", context);
  if (handler) {
    return handler(context);
  }
  // Fallback - no default handler for multiKill yet
  const { onComplete } = context;
  onComplete();
  return <></>;
};

/**
 * Kill streak message handler
 */
const KillStreakHandler = (
  context: MessageAnimationContext
): React.ReactElement => {
  const pack = registry.getPackForEventType("killStreak");
  const handler = pack.getAnimationHandler("killStreak", context);
  if (handler) {
    return handler(context);
  }
  // Fallback - no default handler for killStreak yet
  const { onComplete } = context;
  onComplete();
  return <></>;
};

export const MESSAGE_HANDLERS: Record<string, MessageAnimationHandler> = {
  frag: FragHandler,
  roundWin: RoundWinHandler,
  roundLose: RoundLoseHandler,
  firstBlood: FirstBloodHandler,
  multiKill: MultiKillHandler,
  killStreak: KillStreakHandler,
};
